<script>
	/**
	 * This is a modal that allows a teacher to edit the deadline of
	 * an assignment
	 *
	 * PROPS
	 * - assignment - the assignment to edit
	 *
	 */

	const assignmentService = require('services/assignmentService');
	const moment = require('moment');
	const utilities = require('utilities');
	const { beginAssignmentAction, endAssignmentAction } = require('utilities/assignmentUtilities');
	const model = require('model/model');

	const { authenticateWithGoogleClassroom, isKnownGoogleClass, isInvalidatingGoogleError } = require('utilities/googleClassroomUtilities');

	module.exports = {
		modelData: ['localGCAccessToken', 'gcClassList', 'gcAddon'],
		components: {
			modal: require('./modal.vue').default,
			'validated-form': require('components/validated-form.vue').default,
			'info-icon': require('components/svg/info-icon.vue').default,
			message: require('components/message.vue').default
		},
		data() {
			return {
				assignment: {},
				roster: null,
				edited: false,
				startDate: null,
				deadline: null,
				dueDateTip: 'Students will be able to continue working on Article and Paired Text assignments after the due date passes.',
				endDateTip: 'Students will not be able to continue working on Article-A-Day assignments after the end date passes.',
				loading: false
			};
		},
		computed: {
			startDateFormatted() {
				return moment(this.startDate).format('M/D/YYYY');
			},
			deadlineTitle() {
				let assignment = this.assignment;
				let md = assignment && (assignment.productMetaData || assignment.product);
				let productType = md ? md.productType : null;
				return productType === 'AAD' ? 'End Date' : 'Due Date';
			},
			deadlineTip() {
				let assignment = this.assignment;
				let md = assignment && (assignment.productMetaData || assignment.product);
				let productType = md ? md.productType : null;
				return productType === 'AAD' ? this.endDateTip : this.dueDateTip;
			},
			assignmentPast() {
				let assignment = this.assignment;
				let deadline = assignment ? assignment.deadline : null;
				let now = new Date().getTime();
				return deadline && deadline * 1000 < now;
			},
			minimumDate() {
				return new Date().getTime();
			},
			gcTokenMismatch() {
				const gcid = this.roster && this.roster.gcid;

				return gcid && this.gcClassList && !isKnownGoogleClass(gcid);
			},
			needsGoogleAuth() {
				const assignment = this.assignment;
				const gcid = assignment && assignment.gcid;
				const gca = assignment && assignment.gca && this.gcAddon;
				const localGCAccessToken = this.localGCAccessToken;

				return (gcid || gca) && (!localGCAccessToken || this.gcTokenMismatch);
			}
		},
		watch: {
			assignment(a) {
				if (a) {
					this.startDate = a.startDate * 1000;
					this.deadline = a.deadline * 1000;
				} else {
					this.startDate = null;
					this.deadline = null;
				}
			}
		},
		methods: {
			submitDeadline() {
				let assignment = this.assignment;
				let md = assignment.productMetaData;
				let productType = assignment ? md.productType : null;
				let data = {
					classCode: assignment.classCode,
					assignmentId: assignment.assignmentId,
					productType
				};

				let deadline = moment(this.deadline).startOf('day').add(1, 'days').subtract(1, 'minutes').valueOf();

				data.deadline = deadline;

				if ((this.assignment.gcid || this.assignment.gca) && this.localGCAccessToken) {
					data.token = this.localGCAccessToken;
				}

				beginAssignmentAction();
				this.loading = true;
				return assignmentService
					.updateAssignmentDeadline(data)
					.then(() => utilities.closeModals())
					.catch(error => {
						if (isInvalidatingGoogleError(error)) {
							model.set('localGCAccessToken', '');
						}
					})
					.finally(() => {
						this.loading = false;
						endAssignmentAction();
					});
			},
			authenticateWithGoogle() {
				authenticateWithGoogleClassroom();
			}
		},
		mounted() {
			this.$subscribe('modal-opened', (modalId, opts) => {
				if (modalId !== 'change-deadline-modal') {
					return true; // let some other handler take this
				}
				if (opts) {
					this.assignment = opts.assignment;
					this.roster = opts.roster;
				}
				return false;
			});

			this.$subscribe('modal-closed', (modalId, opts) => {
				if (modalId !== 'change-deadline-modal') {
					return true; // let some other handler take this
				}
				this.assignment = null;
				this.roster = null;
				return false;
			});
		}
	};
</script>
<template>
	<modal
		ref="modal"
		id="change-deadline-modal"
		:loading="loading"
		:ok="false"
		:closeable="!loading"
		:no-auto-close="loading">
		<template slot="header-container">
			<h2>{{ assignmentPast ? 'Extend ' : 'Change ' }} {{ deadlineTitle }}</h2>
			<p class="font-small article-title-subheader">{{ assignment && assignment.title }}</p>
		</template>
		<template slot="content">
			<div
				v-if="assignment && assignment.gca && !gcAddon"
				class="text-centered gca-message"
				slot="content">
				<p>This assignment was set up using Google Classroom Add-ons. Please manage the due date there.</p>
				<button
					class="button -large"
					data-modal-close>
					Ok
				</button>
			</div>
			<validated-form
				v-else
				class="change-deadline-form"
				:on-submit="submitDeadline">
				<div class="form-row dates row">
					<div class="date small-12 medium-6 columns">
						<div class="label">Assignment Start Date</div>
						<div class="start-date form-row-input">
							{{ startDateFormatted }}
						</div>
					</div>
					<div class="date small-12 medium-6 columns">
						<div class="label">
							{{ deadlineTitle }}
							<button
								class="button -link"
								v-tooltip="deadlineTip">
								<info-icon></info-icon>
							</button>
						</div>
						<div class="form-row-input">
							<date-picker
								:date.sync="deadline"
								:min-date="minimumDate"
								:position="'bottom left'">
							</date-picker>
						</div>
					</div>
				</div>
				<div
					class="form-row text-centered"
					v-if="needsGoogleAuth">
					<message
						v-if="gcTokenMismatch"
						class="text-left"
						type="error">
						Please authenticate the Google account used to manage this Classroom
					</message>
					<a
						class="button -large"
						@click="authenticateWithGoogle"
						>Authenticate with Google</a
					>
				</div>
				<button
					class="button -full-width margin-top-medium"
					type="submit">
					Change
				</button>
			</validated-form>
		</template>
	</modal>
</template>
<style scoped lang="scss">
	@import 'config/colors';

	.start-date {
		padding: 8px 0px;
	}

	p.form-instructions {
		text-align: center;
	}

	p.font-small {
		font-weight: normal;
		text-align: center;

		&.article-title-subheader {
			margin: 0.5rem 0rem 0rem 0rem;
		}
	}
</style>
